import {
  createMercadoPagoDepositReservation,
  notifyPaymentFailure,
} from '@features/booking/api/reservations';
import {
  createWlDepositMercadoPago,
} from '@features/booking/api/waitingList';
import type { DepositGrabMercadoPagoPayload } from '@features/booking/api/reservations';
import { forceLoadStripeV3 } from '@features/booking/hooks/useStripeV3';
import { handle3dsChallenge } from '@features/booking/hooks/handle3dsChallenge';
import { env } from '@core/config/env';
import * as Sentry from '@sentry/react';
import { generateRandomDocumentId } from '@shared/utils/randomDocument';
import { readMpDeviceSessionId } from '@shared/utils/mercadoPagoSdkLoader';
import { OTHER_DOCUMENT_TYPE, normalizeDocument } from '@features/booking/utils/documentTypes';
import type { BaseReservationFields, CommitContext, CommitDerived, MpFlowFlagBox } from './shared';
import {
  persistSuccessAndAdvance,
  buildDepositFields,
  assertReservationId,
  MissingReservationIdError,
  PRE_PAYMENT_BACKEND_CODES,
  TIP_STALE_CODES,
} from './shared';

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const obj = err as Record<string, unknown>;
    return (obj.errorMessage as string) || (obj.message as string) || 'Unknown error';
  }
  return 'Unknown error';
}

/**
 * MercadoPago deposit/no-show reservation path — with lazy Stripe v3 load for
 * 3DS.
 *
 * Hard invariants (see plan §3DS-SNAPSHOT §1 + §payment-branches §2):
 * - forceLoadStripeV3 is gated by `was3ds`. It MUST NOT run on happy-path
 *   responses (would needlessly kill the v2 SDK used by the MP form for
 *   future bookings on the same tab).
 * - Use env.stripeFallbackKey directly — BE creates the MP-fallback intent
 *   on platform credentials, not the restaurant's Stripe account.
 *   getStripePublishableKeyForTokenAccount must NOT be used here.
 * - was3ds gate skips the failure notify after a 3DS attempt: the reservation
 *   is already in pending_payment and handle3dsChallenge fired the
 *   non-blocking cancel-3ds-payment. (There is no client-side card-decline
 *   fallback — the backend now creates the reservation for declined no_show
 *   cards on the normal deposit-grab call.)
 * - threeDsResult.reservationId is the post-confirmation ID; use it for
 *   state writes, NOT response.reservationId.
 * - identityDocument is MP-mandatory: the document as typed when the venue
 *   collects one (a string, so a leading zero or a dotted number reaches the
 *   backend intact), otherwise a random one.
 * - mpDeviceSessionId rides along only when the SDK minted one; its absence
 *   is reported as a breadcrumb (has_device_id) and never blocks the booking.
 *
 * The `flagBox.mpFlow3dsLoaded` field is written as soon as v3 loads. It
 * survives a throw because the caller owns the box — see MpFlowFlagBox doc.
 * Without this contract the outer catch couldn't gate setRequireReload and
 * a retry would silently produce a bad v2 token.
 */
export async function commitDepositMercadoPago(
  ctx: CommitContext,
  derived: CommitDerived,
  basePayload: BaseReservationFields,
  flagBox: MpFlowFlagBox,
): Promise<void> {
  const { booking, restaurant } = ctx;
  const { isWaitingList, effectiveGuaranteeType } = derived;

  const guaranteeTypeNarrow = effectiveGuaranteeType as 'deposit' | 'no_show';

  // MercadoPago requires a payer document. The guest's, as typed, when the venue collects
  // one (billing flow); the backend strips separators per the document type. Otherwise a
  // random one, as every MercadoPago charge sent before the document was collected.
  // With a type, the number goes as the card token was minted with it (normalizeDocument), so
  // the token and the charge agree; the backend strips the same way. The token's type wins over the
  // guest's when they differ (`Otro` after a tokenization retry): under it a number with no digits
  // falls back to the random one, as the token did.
  const typedDocument = (booking.identityDocument ?? '').trim();
  const mpDocumentType = booking.mpTokenDocumentType || booking.identityDocumentType || '';
  // No type (a session stored before the type was collected, or a venue that stopped collecting
  // the document): the `Otro` normalisation, digits only, which is what every charge sent before
  // and what the backend applies to an untyped number anyway (review of meitre-booking-app#567).
  // The number the token was minted with wins when it was not the guest's (the random `Otro` one);
  // otherwise the typed number under the token's type, or the random fallback.
  const mpIdentityDocument = booking.mpTokenDocumentNumber
    || normalizeDocument(mpDocumentType || OTHER_DOCUMENT_TYPE, typedDocument)
    || String(generateRandomDocumentId());
  const { id: mpDeviceSessionId, state: deviceIdState } = readMpDeviceSessionId();
  Sentry.addBreadcrumb({
    category: 'mercadopago.payment',
    message: 'MercadoPago deposit payload',
    level: 'info',
    data: { has_device_id: mpDeviceSessionId !== null, device_id: deviceIdState, has_typed_document: typedDocument !== '' },
  });

  const mpPayload: DepositGrabMercadoPagoPayload = {
    ...basePayload,
    date: basePayload.date as string,
    menu: basePayload.menu as number,
    ...buildDepositFields(ctx),
    type: guaranteeTypeNarrow,
    cardToken: booking.cardToken!,
    cardTokenStripe: booking.cardTokenStripe || '',
    paymentMethod: booking.paymentMethod || 'unknown',
    temp1: booking.encryptedCardNumber || '',
    temp3: booking.encryptedExpiry || '',
    temp4: booking.encryptedCvc || '',
    temp6: booking.encryptedHolderName || '',
    identityDocument: mpIdentityDocument,
    ...(mpDeviceSessionId ? { mpDeviceSessionId } : {}),
  };
  // The type goes only with the guest's number. A blank document, or a token minted with the random
  // one, sends no type (the billing fields may carry the guest's: it is dropped here), so the backend
  // keeps counting it as the legacy document (identity_document_source), never a fabricated number
  // presented as a DNI.
  if (mpDocumentType && typedDocument !== '' && !booking.mpTokenDocumentNumber) {
    mpPayload.identityDocumentType = mpDocumentType;
  } else {
    delete mpPayload.identityDocumentType;
  }

  let was3ds = false;
  try {
    const response = isWaitingList
      ? await createWlDepositMercadoPago(
          ctx.currentLanguage,
          { ...mpPayload, date: booking.wlDates },
        )
      : await createMercadoPagoDepositReservation(ctx.currentLanguage, mpPayload);

    was3ds = !!response.requires_action;
    // Write into the caller's box BEFORE any await that could throw.
    // If handle3dsChallenge rejects, the outer commitReservation catch
    // still reads the post-mutation value.
    flagBox.mpFlow3dsLoaded = was3ds;

    // Gate the v3 load behind requires_action. forceLoadStripeV3
    // unloads the v2 SDK (set by the MP form for legacy tokenization)
    // and loads v3 fresh — required because plain loadStripe() would
    // detect the v2 global and return v2, breaking handleNextAction.
    // See forceLoadStripeV3 docstring for the full rationale.
    //
    // Pubkey rationale: BE creates the MP-fallback Stripe intent on the
    // platform (new-account) credentials, not the restaurant's Stripe
    // account. Use env.stripeFallbackKey directly — same key the MP
    // form's v2 hook already uses. Do NOT route via
    // getStripePublishableKeyForTokenAccount: for MP restaurants that
    // resolves to restaurant.publicKeyPayment, which is a MercadoPago
    // key — not a Stripe key — and would crash handleNextAction.
    // T30 overlay: only fire on3dsStart/on3dsEnd when 3DS will actually fire,
    // which the existing ternary already enforces. Wrap the await so the
    // overlay shows during forceLoadStripeV3 + the challenge.
    let threeDsResult = null;
    if (was3ds) {
      ctx.on3dsStart?.();
      try {
        threeDsResult = await handle3dsChallenge(
          response,
          forceLoadStripeV3(env.stripeFallbackKey),
        );
      } finally {
        ctx.on3dsEnd?.();
      }
    }
    if (threeDsResult) {
      const reservationId = assertReservationId(threeDsResult.reservationId, {
        endpoint: 'confirm-3ds-payment',
        isWaitingList,
        flowType: booking.flowType,
        guaranteeType: guaranteeTypeNarrow,
        restaurant: restaurant.id,
      });
      persistSuccessAndAdvance(
        ctx,
        derived,
        reservationId,
        response.uniqueId,
        guaranteeTypeNarrow,
      );
      return;
    }

    const reservationId = assertReservationId(response.reservationId, {
      endpoint: isWaitingList ? 'deposit-waiting-list-mercadopago' : 'deposit-grab-mercadopago',
      isWaitingList,
      flowType: booking.flowType,
      guaranteeType: guaranteeTypeNarrow,
      restaurant: restaurant.id,
    });
    persistSuccessAndAdvance(
      ctx,
      derived,
      reservationId,
      response.uniqueId,
      guaranteeTypeNarrow,
    );
  } catch (mpErr) {
    // A 200-without-reservationId is a backend contract violation, not a payment
    // failure — re-throw to the outer commit catch without firing the payment-
    // failure notify below (which is for genuine declines/errors).
    if (mpErr instanceof MissingReservationIdError) {
      throw mpErr;
    }
    // The backend now creates the reservation when a no_show card is declined,
    // so there's no client-side card-decline fallback. Notify the failure and
    // re-throw to the outer catch where the UI maps the error.
    //
    // 3DS already mutated state (reservation in pending_payment;
    // handle3dsChallenge fired non-blocking cancel-3ds-payment on failure), so
    // skip the notify in that case. flagBox.mpFlow3dsLoaded is already true at
    // this point (set above the throw), so the outer catch will correctly fire
    // setRequireReload.
    //
    // Also skip both pre-charge code sets: GrabController returns all of them
    // before ProcessPayment runs, so there is no card failure to report. See
    // their docblocks in shared.ts.
    const mpErrCode = getErrorMessage(mpErr);
    if (!was3ds && !TIP_STALE_CODES.has(mpErrCode) && !PRE_PAYMENT_BACKEND_CODES.has(mpErrCode)) {
      await notifyPaymentFailure({
        paymentMethod: booking.paymentMethod || undefined,
        errorMsg: mpErrCode,
        paymentProcessor: 'mercadopago',
        restaurantId: restaurant.id,
        billingInfoEntered: 0,
        firstTry: 0,
      }).catch(() => { /* non-blocking */ });
    }
    throw mpErr;
  }
}
